import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {StyledTablePagination} from './StyledTable'

function MuiTablePagination(props) {
  const { count, rowsPerPage, page, onPageChange, onRowsPerPageChange, rowsPerPageOptions = [5, 10, 25, 50] } = props;

  // Total number of pages (at least 1 so the label always reads "Page 1 of 1")
  const pageCount = Math.max(1, Math.ceil(count / rowsPerPage));

  // Local editable value for the "jump to page" input (1-based for display)
  const [pageInput, setPageInput] = useState(String(page + 1));

  // Keep the input in sync when the page changes via the arrow/first/last buttons
  useEffect(() => {
    setPageInput(String(page + 1));
  }, [page]);

  const commitPage = () => {
    const parsed = parseInt(pageInput, 10);
    if (isNaN(parsed)) {
      setPageInput(String(page + 1));
      return;
    }
    // Clamp to a valid range and convert from 1-based input to 0-based page
    const clamped = Math.min(Math.max(parsed, 1), pageCount);
    const newPage = clamped - 1;
    if (newPage !== page) {
      onPageChange(null, newPage);
    }
    setPageInput(String(clamped));
  };

  const handleInputKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitPage();
    } else if (event.key === 'Escape') {
      setPageInput(String(page + 1));
      event.currentTarget.blur();
    }
  };

  // Custom label: keeps the standard "from-to of count" and adds an editable
  // "Page N of M" control. Uses inline elements only, since MUI renders this
  // inside a <p> and block elements there would be invalid DOM nesting.
  const labelDisplayedRows = ({ from, to, count: total }) => {
    const displayedCount = total === -1 ? `more than ${to}` : total;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        <span>{`${from}\u2013${to} of ${displayedCount}`}</span>
        <span style={{ margin: '0 8px', color: 'rgba(0, 0, 0, 0.38)' }}>|</span>
        <span>Page</span>
        <input
          type="text"
          inputMode="numeric"
          value={pageInput}
          onChange={(event) => setPageInput(event.target.value.replace(/[^0-9]/g, ''))}
          onKeyDown={handleInputKeyDown}
          onBlur={commitPage}
          aria-label="current page, type a number and press Enter to jump to that page"
          style={{
            width: `${Math.max(2, pageInput.length + 1)}ch`,
            textAlign: 'center',
            padding: '2px 4px',
            border: '1px solid rgba(0, 0, 0, 0.23)',
            borderRadius: '4px',
            fontSize: 'inherit',
            fontFamily: 'inherit',
            color: 'inherit',
            background: 'white',
          }}
        />
        <span>{`of ${pageCount}`}</span>
      </span>
    );
  };

  return (
       <StyledTablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={count}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={onPageChange}
              onRowsPerPageChange={onRowsPerPageChange}
              showFirstButton
              showLastButton
              labelDisplayedRows={labelDisplayedRows}
              //Important: form labels for accessibility
              SelectProps={{
                inputProps: {
                  'aria-label': 'rows per page',
                  'aria-labelledby': 'rowsPage',
                },
                id: 'rowsPage',
              }}
          />
  )}

MuiTablePagination.propTypes = {
    rowsPerPage: PropTypes.number.isRequired,
    page: PropTypes.number.isRequired,
    count: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    onRowsPerPageChange: PropTypes.func.isRequired,
    rowsPerPageOptions: PropTypes.array,
  };

export default MuiTablePagination